import { useEffect, useState } from "react";
import api from "@/lib/api";
import { PageTitle } from "@/components/CrudPage";
import {
  PhoneCall, Building2, Radio, Users2, PhoneIncoming, ListTree, ArrowUpRight, ArrowDownRight, Activity,
} from "lucide-react";
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";

const GOLD = "#d4af37";

function Stat({ icon: Icon, label, value, hint }) {
  return (
    <div className="rounded-xl border border-border bg-card p-5 card-hover" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{label}</span>
        <div className="w-9 h-9 rounded-lg grid place-items-center bg-[hsl(var(--gold)/0.12)] text-[hsl(var(--gold))]">
          <Icon size={17} />
        </div>
      </div>
      <div className="mt-3 text-3xl font-display font-bold">{value}</div>
      {hint && <div className="text-xs text-muted-foreground mt-1">{hint}</div>}
    </div>
  );
}

export default function Dashboard() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/dashboard/stats").then((r) => setS(r.data)); }, []);

  if (!s) return <div className="text-muted-foreground">Loading dashboard…</div>;
  const c = s.counts;
  const dispData = Object.entries(s.dispositions || {}).map(([k, v]) => ({ name: k, value: v }));
  const dispColors = { "ANSWERED": "#3ba776", "NO ANSWER": GOLD, "BUSY": "#c98a2b", "FAILED": "#c0392b" };

  return (
    <div>
      <PageTitle title="Dashboard" subtitle="Live overview of your InContact Cloud PBX." />

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Stat icon={PhoneCall} label="Extensions" value={c.extensions} hint="SIP endpoints" />
        <Stat icon={Building2} label="Departments" value={c.departments} hint="Isolated groups" />
        <Stat icon={Radio} label="SIP Trunks" value={c.trunks} hint="Provider links" />
        <Stat icon={Activity} label="Active Calls" value={s.active_calls} hint="Live now" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-4">
        <div className="lg:col-span-2 rounded-xl border border-border bg-card p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display font-semibold">Call Volume · Last 7 days</h3>
            <div className="flex gap-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><i className="w-2 h-2 rounded-full inline-block" style={{ background: GOLD }} />Inbound</span>
              <span className="flex items-center gap-1"><i className="w-2 h-2 rounded-full inline-block bg-blue-400" />Outbound</span>
              <span className="flex items-center gap-1"><i className="w-2 h-2 rounded-full inline-block bg-emerald-400" />Internal</span>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={s.per_day} margin={{ left: -20, right: 8 }}>
              <defs>
                <linearGradient id="gGold" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={GOLD} stopOpacity={0.5} /><stop offset="100%" stopColor={GOLD} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" vertical={false} />
              <XAxis dataKey="date" stroke="#8a8a8a" fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke="#8a8a8a" fontSize={12} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: "#141416", border: "1px solid #2a2a2e", borderRadius: 10 }} />
              <Area type="monotone" dataKey="inbound" stroke={GOLD} strokeWidth={2} fill="url(#gGold)" />
              <Area type="monotone" dataKey="outbound" stroke="#60a5fa" strokeWidth={2} fillOpacity={0} />
              <Area type="monotone" dataKey="internal" stroke="#34d399" strokeWidth={2} fillOpacity={0} />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-xl border border-border bg-card p-5">
          <h3 className="font-display font-semibold mb-4">Call Outcomes</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={dispData} layout="vertical" margin={{ left: 20 }}>
              <XAxis type="number" hide />
              <YAxis type="category" dataKey="name" width={80} stroke="#8a8a8a" fontSize={11} tickLine={false} axisLine={false} />
              <Tooltip contentStyle={{ background: "#141416", border: "1px solid #2a2a2e", borderRadius: 10 }} cursor={{ fill: "#ffffff08" }} />
              <Bar dataKey="value" radius={[0, 6, 6, 0]}>
                {dispData.map((d) => <Cell key={d.name} fill={dispColors[d.name] || GOLD} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-lg bg-secondary/40 p-3">
              <div className="text-xs text-muted-foreground">Answer rate</div>
              <div className="text-xl font-display font-bold text-emerald-400">{s.answer_rate}%</div>
            </div>
            <div className="rounded-lg bg-secondary/40 p-3">
              <div className="text-xs text-muted-foreground">Calls today</div>
              <div className="text-xl font-display font-bold">{s.calls_today}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mt-4">
        <Stat icon={Users2} label="Users" value={c.users} hint="Console accounts" />
        <Stat icon={PhoneIncoming} label="Call Queues" value={c.queues} hint="Agent queues" />
        <Stat icon={ListTree} label="IVR Menus" value={c.ivrs} hint="Auto-attendants" />
      </div>
    </div>
  );
}
